import { guards } from '@shared/helpers/typeguards.helpers';

const CONSOLE_IMAGE_PREVIEW_MAX_EDGE_PX = 480;

/**
 * Decode an image URL so the bitmap is in memory before paint / PDF capture.
 * Uses {@link HTMLImageElement.decode} when present, otherwise `load`/`error`.
 * Sets `src` after wiring handlers so a cached image cannot miss `onload`.
 */
export const decodeImageUrl = (url: string): Promise<void> => {
  const image = new Image();
  if (guards.is.function(image.decode)) {
    image.src = url;
    return image.decode();
  }
  return new Promise((resolve, reject) => {
    const fail = () => reject(new Error('Failed to decode image'));
    image.onload = () => resolve();
    image.onerror = fail;
    image.src = url;
    if (!image.complete) return;
    if (image.naturalWidth > 0) {
      resolve();
      return;
    }
    fail();
  });
};

/**
 * Print a data-URL image in DevTools so the map screenshot sent to AI can be previewed.
 */
export const logImageDataUrlToConsole = (
  label: string,
  dataUrl: string,
): void => {
  const image = new Image();
  image.onload = () => {
    const { naturalWidth: width, naturalHeight: height } = image;
    const scale = Math.min(
      1,
      CONSOLE_IMAGE_PREVIEW_MAX_EDGE_PX / Math.max(width, height, 1),
    );
    const previewWidth = Math.max(1, Math.round(width * scale));
    const previewHeight = Math.max(1, Math.round(height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = previewWidth;
    canvas.height = previewHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      console.info(label, `${width}×${height}`, image);
      return;
    }
    context.drawImage(image, 0, 0, previewWidth, previewHeight);
    const previewUrl = canvas.toDataURL('image/jpeg', 0.7);
    console.info(`${label} (${width}×${height})`);
    // DevTools renders a CSS background as an inline thumbnail.
    console.info(
      '%c ',
      [
        `background: url("${previewUrl}") no-repeat center / contain`,
        `padding: ${Math.round(previewHeight / 2)}px ${Math.round(previewWidth / 2)}px`,
        'font-size: 1px',
        'line-height: 0',
      ].join('; '),
    );
  };
  image.onerror = () => {
    console.info(label, 'failed to load image for console preview');
  };
  image.src = dataUrl;
};
